import { useState } from "react";
import { motion } from "motion/react";
import {
    ArrowLeft, ExternalLink, Github, Calendar, MapPin, Users,
    Target, CheckCircle, Star, ChevronLeft, ChevronRight,
    Award, TrendingUp, Zap, Shield, Code, Monitor,
    BarChart, Clock
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { ImageWithFallback } from "../../ui/imageWithFallback";
import { useParams } from 'react-router-dom';
import projects from '../../data/projects';
import NotFoundPage from '../../common/NotFoundPage';

export function PortfolioDetailPage({ portfolioId, setCurrentPage }: any) {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    const { id } = useParams();
    const projectId = Number(id);

    const found:any = projects.find((p: any) => p.id === projectId);
    if (!found) return <NotFoundPage />;

    const currentProject:any = {
        id: found.id,
        title: found.title,
        category: found.category,
        client: found.client,
        duration: found.duration || '—',
        teamSize: found.teamSize || '—',
        location: found.location || '',
        shortDescription: found.description || '',
        fullDescription: found.fullDescription || (found.description ? `<p>${found.description}</p>` : ''),
        images: found.images ?? (found.image ? [found.image] : []),
        technologies: found.tags ?? [],
        features: found.features ?? [],
        challenges: found.challenges ?? [],
        results: found.results ?? [],
        testimonial: found.testimonial ?? { quote: '', author: '', role: '', avatar: '' },
        projectUrl: found.liveUrl ?? '',
        githubUrl: found.githubUrl ?? '',
        awards: found.awards ?? [],
        completedAt: found.completedAt || new Date().toISOString(),
        seoTitle: found.seoTitle || found.title,
        seoDescription: found.seoDescription || '',
        metaKeywords: found.metaKeywords || ''
    };

    const relatedProjects:any = [];

    // const relatedProjects = [
    //     {
    //         id: 2,
    //         title: "E-commerce AI Assistant",
    //         category: "Artificial Intelligence",
    //         image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop",
    //         description: "AI-powered shopping assistant that increased conversion rates by 180%"
    //     },
    //     {
    //         id: 3,
    //         title: "Healthcare Management System",
    //         category: "Healthcare Technology",
    //         image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400&h=300&fit=crop",
    //         description: "Comprehensive patient management platform serving 500+ healthcare providers"
    //     },
    //     {
    //         id: 4,
    //         title: "Smart Manufacturing Platform",
    //         category: "IoT & Industry 4.0",
    //         image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop",
    //         description: "IoT-enabled manufacturing optimization system reducing waste by 35%"
    //     }
    // ];

    

    const handleBackToPortfolio = () => {
        setCurrentPage("portfolio");
    };

    const nextImage = () => {
        setSelectedImageIndex((prev) =>
            prev === currentProject.images.length - 1 ? 0 : prev + 1
        );
    };

    const prevImage = () => {
        setSelectedImageIndex((prev) =>
            prev === 0 ? currentProject.images.length - 1 : prev - 1
        );
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <article className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
            {/* Back Navigation */}
            <section className="px-6 py-8 max-w-7xl mx-auto">
                <Button
                    variant="ghost"
                    onClick={handleBackToPortfolio}
                    className="text-slate-600 hover:text-blue-600 mb-6"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Portfolio
                </Button>
            </section>

            {/* Project Hero */}
            <section className="px-6 pb-16 max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    {/* Project Meta */}
                    {/* <div className="flex flex-wrap items-center gap-4 mb-8">
                        <Badge className="bg-blue-600 text-white text-sm">
                            {currentProject.category}
                        </Badge>
                        <div className="flex items-center gap-2 text-slate-500">
                            <Calendar className="w-4 h-4" />
                            Completed {formatDate(currentProject.completedAt)}
                        </div>
                        <div className="flex items-center gap-2 text-slate-500">
                            <MapPin className="w-4 h-4" />
                            {currentProject.location}
                        </div>
                        <div className="flex items-center gap-2 text-slate-500">
                            <Clock className="w-4 h-4" />
                            {currentProject.duration}
                        </div>
                    </div> */}

                    {/* Project Title */}
                    <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                        {currentProject.title}
                    </h1>

                    {/* Project Description */}
                    <p className="text-xl text-slate-600 leading-relaxed mb-8 max-w-4xl">
                        {currentProject.shortDescription}
                    </p>

                    {/* Project Info Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                                    <Users className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-slate-900">Client</h3>
                                    <p className="text-slate-600 text-sm">{currentProject.client}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                                    <Code className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-slate-900">Team Size</h3>
                                    <p className="text-slate-600 text-sm">{currentProject.teamSize}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                                    <Calendar className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-slate-900">Duration</h3>
                                    <p className="text-slate-600 text-sm">{currentProject.duration}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-4">
                        {currentProject.projectUrl && (
                            <a href={currentProject.projectUrl} target="_blank" rel="noopener noreferrer">
                                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    View Live Project
                                </Button>
                            </a>
                        )}

                        {currentProject.githubUrl && (
                            <a href={currentProject.githubUrl} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50">
                                    <Github className="w-4 h-4 mr-2" />
                                    View Source Code
                                </Button>
                            </a>
                        )}
                    </div>
                </motion.div>
            </section>

            {/* Project Images Gallery */}
            {currentProject.images && currentProject.images.length > 0 && (
            <section className="px-6 pb-16 max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    {/* Main Image */}
                    <div className="relative mb-6">
                        <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-slate-100">
                            <ImageWithFallback
                                src={currentProject.images[selectedImageIndex]}
                                alt={`${currentProject.title} - Image ${selectedImageIndex + 1}`}
                                className="w-full h-[600px] object-cover"
                            />

                            {/* Navigation Arrows */}
                            <button
                                onClick={prevImage}
                                className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-lg"
                            >
                                <ChevronLeft className="w-6 h-6 text-slate-700" />
                            </button>
                            <button
                                onClick={nextImage}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors shadow-lg"
                            >
                                <ChevronRight className="w-6 h-6 text-slate-700" />
                            </button>

                            {/* Image Counter */}
                            <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-sm">
                                {selectedImageIndex + 1} / {currentProject.images.length}
                            </div>
                        </div>
                    </div>

                    {/* Thumbnail Gallery */}
                    <div className="flex gap-4 overflow-x-auto pb-2">
                        {currentProject.images.map((image:any, index:any) => (
                            <button
                                key={index}
                                onClick={() => setSelectedImageIndex(index)}
                                className={`flex-shrink-0 relative overflow-hidden rounded-xl transition-all duration-300 ${index === selectedImageIndex
                                        ? 'ring-4 ring-blue-500 scale-105'
                                        : 'hover:scale-102 opacity-70 hover:opacity-100'
                                    }`}
                            >
                                <ImageWithFallback
                                    src={image}
                                    alt={`${currentProject.title} thumbnail ${index + 1}`}
                                    className="w-24 h-16 object-cover"
                                />
                            </button>
                        ))}
                    </div>
                </motion.div>
            </section>
            )}

            {/* Project Details */}
            <section className="px-6 pb-16 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 mb-8"
                        >
                            <h2 className="text-3xl font-bold text-slate-900 mb-6">Project Details</h2>
                            <div
                                className="prose prose-slate prose-lg max-w-none"
                                dangerouslySetInnerHTML={{ __html: currentProject.fullDescription }}
                            />
                        </motion.div>
        
                        {currentProject.features && currentProject.features.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 mb-8"
                        >
                            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                <Target className="w-7 h-7 text-blue-600" />
                                Key Features
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {currentProject.features.map((feature:any, index:any) => (
                                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                                        <span className="text-slate-700">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                        )}

                        {/* Challenges & Solutions */}
                        {currentProject.challenges && currentProject.challenges.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8"
                            >
                            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                <Zap className="w-7 h-7 text-yellow-600" />
                                Challenges & Solutions
                            </h2>
                            <div className="space-y-6">
                                {currentProject.challenges.map((challenge:any, index:any) => (
                                    <div key={index} className="border-l-4 border-blue-500 pl-6">
                                        <h3 className="text-lg font-semibold text-slate-900 mb-2">
                                            {challenge.title}
                                        </h3>
                                        <p className="text-slate-600 leading-relaxed">
                                            {challenge.description}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>)}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-8">
                        {/* Technologies Used */}
                        {currentProject.technologies && currentProject.technologies.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6"
                        >
                            <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                <Monitor className="w-6 h-6 text-blue-600" />
                                Technologies Used
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {currentProject.technologies.map((tech:any) => (
                                    <Badge
                                        key={tech}
                                        variant="secondary"
                                        className="bg-blue-100 text-blue-700 hover:bg-blue-200"
                                    >
                                        {tech}
                                    </Badge>
                                ))}
                            </div>
                        </motion.div>
                        )}

                        {/* Project Results */}
                        {currentProject.results && currentProject.results.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6"
                        >
                            <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                <BarChart className="w-6 h-6 text-green-600" />
                                Project Results
                            </h3>
                            <div className="space-y-4">
                                {currentProject.results.map((result:any, index:any) => (
                                    <div key={index} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-white">
                                            {result.icon}
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-slate-900">{result.metric}</div>
                                            <div className="text-sm text-slate-600">{result.description}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                        )}

                        {/* Awards */}
                        {currentProject.awards && currentProject.awards.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6"
                            >
                                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                    <Award className="w-6 h-6 text-yellow-600" />
                                    Awards & Recognition
                                </h3>
                                <div className="space-y-3">
                                    {currentProject.awards.map((award:any, index:any) => (
                                        <div key={index} className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                            <Star className="w-5 h-5 text-yellow-600" />
                                            <span className="text-slate-700 font-medium">{award}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </div>
                </div>
            </section>

            {/* Client Testimonial */}
            {currentProject.testimonial && currentProject.testimonial.quote && (
            <section className="px-6 pb-16 max-w-4xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white"
                >
                    <div className="flex items-start gap-6">
                        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <Star className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <blockquote className="text-xl italic mb-4 leading-relaxed">
                                "{currentProject.testimonial.quote}"
                            </blockquote>
                            <div className="flex items-center gap-4">
                                {currentProject.testimonial.avatar && (
                                    <ImageWithFallback
                                        src={currentProject.testimonial.avatar}
                                        alt={currentProject.testimonial.author}
                                        className="w-12 h-12 rounded-full border-2 border-white/30"
                                    />
                                )}
                                <div>
                                    <div className="font-semibold text-white">{currentProject.testimonial.author}</div>
                                    <div className="text-blue-200">{currentProject.testimonial.role}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </section>
            )}

            {/* Related Projects */}
            {relatedProjects && relatedProjects.length > 0 && (
            <section className="px-6 pb-24 max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-3xl font-bold text-slate-900 mb-8">Related Projects</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {relatedProjects.map((project:any, index:any) => (
                            <motion.div
                                key={project.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                            >
                                <div className="relative overflow-hidden h-48">
                                    <ImageWithFallback
                                        src={project.image}
                                        alt={project.title}
                                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                                    />
                                    <Badge className="absolute top-4 left-4 bg-white text-slate-700">
                                        {project.category}
                                    </Badge>
                                </div>
                                <div className="p-6">
                                    <h3 className="text-lg font-semibold text-slate-900 mb-3 hover:text-blue-600 transition-colors">
                                        {project.title}
                                    </h3>
                                    <p className="text-slate-600 text-sm mb-4">
                                        {project.description}
                                    </p>
                                    <Button
                                        variant="ghost"
                                        className="text-blue-600 hover:text-blue-700 p-0 h-auto font-medium group"
                                    >
                                        View Project
                                        <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </section>
            )}

            {/* CTA Section */}
            <section className="px-6 pb-24 max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-3xl p-12 text-center text-white"
                >
                    <h2 className="text-3xl font-bold mb-4">
                        Ready to Start Your Project?
                    </h2>
                    <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
                        Let's discuss how we can help you build innovative solutions that drive real business results,
                        just like we did for {currentProject.client}.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button className="bg-white text-slate-900 hover:bg-blue-50 px-8">
                            Start a Project
                        </Button>
                        <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8">
                            Schedule Consultation
                        </Button>
                    </div>
                </motion.div>
            </section>
        </article>
    );
}